import {
	Component,
	EventEmitter,
	Input,
	OnInit,
	Output
}                          from '@angular/core';
import { AbstractControl } from '@angular/forms';
import { BaseComponent }   from '@core/bases/base-component/base.component';
import {
	ContinentI18n,
	CONTINENTS,
	ContinentsEnum
}                          from '@shared/components/country/models/country-continent.model';
import {
	CountriesEnum,
	Country,
	CountryI18n
}                          from '@shared/components/country/models/country-country.model';
import {
	skipWhile,
	take,
	takeUntil
}                          from 'rxjs/operators';

@Component( {
	selector: 'app-country',
	template: '<ng-content></ng-content>',
	styleUrls: [ './country.component.scss' ]
} )
export class CountryComponent extends BaseComponent implements OnInit {

	private readonly _allCountries: Country[] = Object.values( CountriesEnum ).map(
		( countryIdentifier: CountriesEnum ): Country => new Country( countryIdentifier )
	);
	private _allowedCurrentCountry: Country;
	private _continentsI18n: ContinentI18n;
	private _countryList: Country[] = [];

	/**
	 * @Input properties for configuration.
	 * Country or continent exclusion has priority over disabling.
	 * If nothing is passed in here, `_countryList` will hold all countries of the world.
	 */
	@Input() allowedCountries: CountriesEnum[];
	@Input() allowedContinents: ContinentsEnum[];
	@Input() excludedCountries: CountriesEnum[];
	@Input() excludedContinents: ContinentsEnum[];
	@Input() disabledCountries: CountriesEnum[];
	@Input() disabledContinents: ContinentsEnum[];

	@Input() countryControl: AbstractControl;

	/**
	 * Flag that, if `true`, allows to add a country to `_countryList` which
	 * is not yet in this list because not among the countries allowed per config
	 * (relevant in "update" forms, when country ID is loaded into the nested form
	 * control from a pre-existing data record).
	 */
	@Input() allowCurrent = true;

	@Output() countryListEvent: EventEmitter<Country[]> = new EventEmitter<Country[]>();

	constructor() {
		super();
	}

	private fillCountryList(): void {
		this._countryList = [];

		// all by default
		if ( !this.allowedCountries && !this.allowedContinents ) {
			this._countryList = this._allCountries;
		}

		if ( this.allowedCountries ) {
			this._countryList = this._allCountries.filter( ( country: Country ) =>
				this.allowedCountries.includes( country.countryIdentifier )
			);
		}

		if ( this.allowedContinents ) {
			this.allowedContinents.forEach( ( continentId: ContinentsEnum ) => {
				CONTINENTS.get( continentId ).forEach( ( countryId: CountriesEnum ) => {
					const isCountryInList: boolean = this._countryList.some(
						( country: Country ) => country.countryIdentifier === countryId
					);
					if ( !isCountryInList ) {
						this._countryList.push( new Country( countryId ) );
					}
				} );
			} );
		}

		/*
		 * order is important from here on:
		 * exclude & disable first, then add current, i18n & sort last
		 */
		this.processDisabled();
		this.filterExcluded();

		// add country that (by config) is not in current set of countries
		if (
			this.allowCurrent &&
			this._allowedCurrentCountry &&
			!this._countryList.includes( this._allowedCurrentCountry )
		) {
			this._countryList.push( this._allowedCurrentCountry );
		}

		this.fillTranslations();
		this.sortCountries();

		this.countryListEvent.emit( this._countryList );
	}

	private processDisabled(): void {
		if ( this.disabledCountries?.length > 0 || this.disabledContinents?.length > 0 ) {

			this._countryList.forEach( ( country: Country ) => {

				// countries
				if ( this.disabledCountries?.length > 0 && this.disabledCountries.includes( country.countryIdentifier ) ) {
					country.disabled = true;
				}

				// continents
				if ( this.disabledContinents?.length > 0 ) {
					this.disabledContinents.forEach( ( continentId: ContinentsEnum ) => {
						if ( CONTINENTS.get( continentId ).has( country.countryIdentifier ) ) {
							country.disabled = true;
						}
					} );
				}
			} );
		}
	}

	private filterExcluded(): void {
		this._countryList = this._countryList.filter( ( country: Country ) => {
			const isCountryExcluded =
				this.excludedCountries && this.excludedCountries.includes( country.countryIdentifier ) ||
				this.excludedContinents && this.excludedContinents.some(
																 ( continentId: ContinentsEnum ) => CONTINENTS.get( continentId ).has( country.countryIdentifier )
															 );

			return !isCountryExcluded;
		} );
	}

	private fillTranslations(): void {
		const continentsI18n: ContinentI18n = this.getI18nString( false, 'continents' ) as ContinentI18n;
		const countriesI18n: CountryI18n = this.getI18nString( false, 'countries' ) as CountryI18n;

		if ( this.countryControl ) {
			this._continentsI18n = continentsI18n;

			this._countryList?.forEach( ( country: Country ) => {
				const countryI18n: string = countriesI18n[ country.countryIdentifier ];
				const continentName: string = this.getContinentTranslateForCountry( country );

				country.nameLocalized = countryI18n;
				country.continentLocalized = continentName;
			} );
		}

	}

	private getContinentTranslateForCountry( country: Country ): string {
		const continentId: ContinentsEnum = this.getContinentForCountry( country );
		const continentI18nResult = this._continentsI18n[ continentId ];
		return continentI18nResult;
	}

	private getContinentForCountry( country: Country ): ContinentsEnum {
		return [ ...CONTINENTS.keys() ].find( ( continentId: ContinentsEnum ) =>
			CONTINENTS.get( continentId ).has( country.countryIdentifier )
		);
	}

	private sortCountries(): void {
		this._countryList.sort( ( countryA: Country, countryB: Country ) =>
			countryA.nameLocalized?.localeCompare( countryB.nameLocalized )
		);

	}

	ngOnInit(): void {
		this.fillCountryList();

		// updates countryList on language change
		this.uiService.languageHasChanged$
		.pipe( takeUntil( this.ngUnsubscribe ) )
		.subscribe( () => {
			this.fillCountryList();
		} );

		this.countryControl?.valueChanges
		.pipe(
			takeUntil( this.ngUnsubscribe ),
			skipWhile( ( countryCode: CountriesEnum ) => !countryCode ),
			take( 1 )
		)
		.subscribe( ( countryCode: CountriesEnum ) => {
			if ( !countryCode ) { return; }

			const isCurrentCountryListed = this._countryList.find(
				( country: Country ) => country.countryIdentifier === countryCode
			);

			// add country to list -- even if excluded by config -- if such exceptions are allowed (by config)
			if ( !isCurrentCountryListed && this.allowCurrent ) {
				this._allowedCurrentCountry = new Country( countryCode );
				this.fillCountryList();
				return;
			}
		} );
	}
}
